import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CmsService {
  // ButterCMS constants
  readonly BUTTERCMS_BASE_URL = 'https://api.buttercms.com/v2';
  readonly NG_APP_ANGULAR_BUTTER_CMS_API_KEY =
    '82badedb124b4e6f91da50da9614bab11df57d08';

  private http!: HttpClient;
  private route!: ActivatedRoute;

  constructor() {
    this.http = inject(HttpClient);
    this.route = inject(ActivatedRoute);
  }

  // Methods

  // Get a single page from ButterCMS
  getPage(pageType: string, pageSlug: string) {
    return this.route.queryParams.pipe(
      switchMap((params) => {
        const buildParams: ParamsForButterPage = {
          pageType: pageType,
          pageSlug: pageSlug,
          isPreview: params['preview'] === '1',
        };

        const url = this.buildButterUrlForPage(buildParams);

        return this.http.get<any>(url).pipe(
          map((response) => {
            return response.data.fields;
          })
        );
      })
    );
  }

  // Get a single content item from a collection
  getContentItem(collectionSlug: string, itemId: number) {
    return this.route.queryParams.pipe(
      switchMap((params) => {
        const buildParams: ParamsForButterItem = {
          collectionSlug: collectionSlug,
          itemId: itemId,
          isPreview: params['preview'] === '1',
        };

        const url = this.buildButterUrlForItem(buildParams);

        return this.http.get<any>(url).pipe(
          map((response) => {
            switch (collectionSlug) {
              case 'quickfact':
                if (
                  response?.data?.quickfact &&
                  response.data.quickfact.length > 0
                ) {
                  return response.data.quickfact[0];
                }
                return null;
              case 'educational_article':
                if (
                  response?.data?.educational_article &&
                  response.data.educational_article.length > 0
                ) {
                  return response.data.educational_article[0];
                }
                return null;
              case 'news_article':
                if (
                  response?.data?.news_article &&
                  response.data.news_article.length > 0
                ) {
                  return response.data.news_article[0];
                }
                return null;
            }
          })
        );
      })
    );
  }

  // Gets all items from a collection
  getCollection(collectionSlug: string) {
    return this.route.queryParams.pipe(
      switchMap((params) => {
        const buildParams: ParamsForButterCollection = {
          collectionSlug: collectionSlug,
          isPreview: params['preview'] === '1',
        };

        const url = this.buildButterUrlForCollection(buildParams);

        return this.http.get<any>(url).pipe(
          map((response) => {
            return response.data;
          })
        );
      })
    );
  }

  // Gets all items from a collection without depending on the router, so it can run
  // before the first navigation (e.g. from an app initializer)
  getCollectionData<T>(collectionSlug: string, isPreview = false) {
    const url = this.buildButterUrlForCollection({ collectionSlug, isPreview });

    return this.http.get<{ data: T }>(url).pipe(map((response) => response.data));
  }

  // URL Builders for ButterCMS

  private buildButterUrlForPage(params: ParamsForButterPage): string {
    let url = this.BUTTERCMS_BASE_URL;

    params.levels = params.levels || 3;

    url += `/pages/${params.pageType}/${params.pageSlug}?levels=${params.levels}&`;

    url += `auth_token=${this.NG_APP_ANGULAR_BUTTER_CMS_API_KEY}`;

    url += params.isPreview ? '&preview=1' : '';

    return url;
  }

  private buildButterUrlForCollection(
    params: ParamsForButterCollection
  ): string {
    let url = this.BUTTERCMS_BASE_URL;

    params.levels = params.levels || 3;

    url += `/content/${params.collectionSlug}?levels=${params.levels}&`;

    url += `auth_token=${this.NG_APP_ANGULAR_BUTTER_CMS_API_KEY}`;

    url += params.isPreview ? '&preview=1' : '';

    return url;
  }

  private buildButterUrlForItem(params: ParamsForButterItem): string {
    let url = this.BUTTERCMS_BASE_URL;

    params.levels = params.levels || 3;

    url += `/content/?keys=${params.collectionSlug}[_id=${params.itemId}]&levels=${params.levels}&`;

    url += `auth_token=${this.NG_APP_ANGULAR_BUTTER_CMS_API_KEY}`;

    url += params.isPreview ? '&preview=1' : '';

    return url;
  }
}

interface ParamsForButterPage {
  pageType: string;
  pageSlug: string;
  levels?: number;
  isPreview?: boolean | undefined;
}

interface ParamsForButterCollection {
  collectionSlug: string;
  levels?: number;
  isPreview?: boolean | undefined;
}

interface ParamsForButterItem {
  collectionSlug: string;
  itemId: number;
  levels?: number;
  isPreview?: boolean | undefined;
}
